import { useCustomerIO } from './providers/useCustomerIO';
import { useGoogleAnalytics } from './providers/useGoogleAnalytics';
import { usePostHog } from './providers/usePostHog';
import { NestedUser, NestedAgency } from '@habity/types';
import { transformUserToTrackingData } from '~/lib/tracking';
import {
  CustomerIOConfig,
  GoogleAnalyticsConfig,
  PostHogConfig,
} from '@habity/types';
import { getProviderSettings, hasProvider } from '@habity/agencies';

interface EventProperties {
  [key: string]: unknown;
}

export type TrackingConfig = {
  juneWriteKey?: string;
  customerIOWriteKey?: string;
  gaId?: string;
  posthogApiKey?: string;
  posthogHost?: string;
};

// Track which users have been identified
const identifiedUsers = new Set<string>();

// Keep track of recent tracking events to avoid duplicates
const recentTrackingEvents = new Map<string, number>();
const TRACKING_DEDUP_WINDOW_MS = 600; // 600ms window to prevent duplicates

// Keep track of initialization promise
let initializationPromise: Promise<void> | null = null;

// Shared initialized state across all hook instances
let initialized = false;
const setInitializedCallbacks = new Set<(value: boolean) => void>();

const setGlobalInitialized = (value: boolean) => {
  initialized = value;
  setInitializedCallbacks.forEach((callback) => callback(value));
};

export function useTracking() {
  const {
    init: initCustomerIO,
    track: trackCustomerIO,
    identify: identifyCustomerIO,
  } = useCustomerIO();
  const {
    init: initGA,
    track: trackGA,
    identify: identifyGA,
  } = useGoogleAnalytics();
  const {
    init: initPostHog,
    track: trackPostHog,
    identify: identifyPostHog,
  } = usePostHog();

  const showTrackingLogs =
    process.env.NEXT_PUBLIC_SHOW_TRACKING_LOGS === 'true';

  // Initialize tracking services directly from agency settings
  const initTracking = async (
    agency: NestedAgency | null,
    user?: NestedUser | null
  ) => {
    if (!agency || typeof window === 'undefined') return;

    if (user) {
      // Only identify if not already identified
      const userKey = `user-${user.id}`;
      if (!identifiedUsers.has(userKey)) {
        identifiedUsers.add(userKey);
        identify(user);
      }
    }

    // If initialization is already in progress, wait for it to complete
    if (initializationPromise) {
      await initializationPromise;
      // After initialization completes, identify the user if provided
      if (user) {
        const userKey = `user-${user.id}`;
        if (!identifiedUsers.has(userKey)) {
          identifiedUsers.add(userKey);
          identify(user);
        }
      }
      return;
    }
    // Create a promise to track initialization
    initializationPromise = (async () => {
      const initPromises = [];

      // Add Customer IO tracking if available
      if (hasProvider(agency, 'customer_io')) {
        const customerIOSettings = getProviderSettings(
          agency,
          'customer_io'
        ) as CustomerIOConfig;
        const { writeKey } = customerIOSettings || {};
        if (writeKey) {
          initPromises.push(initCustomerIO(writeKey));
        }
      }

      // Add Google Analytics tracking if available
      if (hasProvider(agency, 'google_analytics')) {
        const gaSettings = getProviderSettings(
          agency,
          'google_analytics'
        ) as GoogleAnalyticsConfig;
        const { gaId } = gaSettings || {};
        if (gaId) {
          initPromises.push(initGA(gaId));
        }
      }

      // Add PostHog tracking if available
      if (hasProvider(agency, 'posthog')) {
        const posthogSettings = getProviderSettings(
          agency,
          'posthog'
        ) as PostHogConfig;
        const { apiKey, host } = posthogSettings || {};
        if (apiKey) {
          initPromises.push(initPostHog({ apiKey, host }));
        }
      }

      try {
        // Wait for all initialization to complete
        await Promise.all(initPromises);

        if (showTrackingLogs && process.env.NODE_ENV !== 'production') {
          console.log('Tracking init: All providers initialized successfully');
        }
        setGlobalInitialized(true);
      } catch (error) {
        console.error('Tracking init: Error during initialization:', error);
        // Still set initialized to true so tracking doesn't get completely blocked
        // Individual providers that failed will be handled by their own error handling
        setGlobalInitialized(true);
      }
    })();

    try {
      // Wait for initialization to complete
      await initializationPromise;
      initializationPromise = null;

      // If user is provided, identify them after initialization
      if (user) {
        const userKey = `user-${user.id}`;
        identifiedUsers.add(userKey);
        identify(user);
      }
    } catch (error) {
      console.error('Tracking init: Error waiting for initialization:', error);
      initializationPromise = null;
    }
  };

  // Identify user in all tracking services
  const identify = (user?: NestedUser | null) => {
    if (!user || typeof window === 'undefined') return;

    const userData = transformUserToTrackingData(user);

    // Identify in Customer IO (fire-and-forget async)
    identifyCustomerIO(user.id, userData).catch((error) =>
      console.error('Error identifying in Customer IO:', error)
    );

    // Identify in Google Analytics
    identifyGA(user.id, userData);

    // Identify in PostHog
    identifyPostHog(user.id, userData);
  };

  // Track event in all tracking services
  const track = (eventName: string, properties?: EventProperties) => {
    if (typeof window === 'undefined') return;

    // Get current timestamp for the event
    const timestamp = new Date().toISOString();

    // Normalize userId to string if present (handles both number and string)
    const normalizedUserId =
      properties?.userId !== undefined && properties?.userId !== null
        ? properties.userId.toString()
        : undefined;

    // Add environment information and timestamp to all tracking events
    const enhancedProperties = {
      ...properties,
      userId: normalizedUserId, // Ensure userId is always a string
      environment: process.env.NODE_ENV || 'unknown',
      timestamp, // Always include the timestamp with every event
    };

    // Check for recent tracking events to avoid duplicates
    const eventKey = `${eventName}-${JSON.stringify(enhancedProperties)}`;
    const now = Date.now();
    const recentEvent = recentTrackingEvents.get(eventKey);
    if (recentEvent && now - recentEvent < TRACKING_DEDUP_WINDOW_MS) {
      return;
    }
    recentTrackingEvents.set(eventKey, now);

    // Track in Customer IO (fire-and-forget async)
    trackCustomerIO(eventName, enhancedProperties).catch((error) =>
      console.error('Error tracking in Customer IO:', error)
    );

    // Track in Google Analytics
    trackGA(eventName, enhancedProperties);

    // Track in PostHog
    trackPostHog(eventName, enhancedProperties);
  };

  return {
    init: initTracking,
    identify,
    track,
    initialized,
  };
}
